import { useState } from "react";
import Topbar from "@/components/layout/Topbar";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

export default function SuperBookingEngine() {
    const [form, setForm] = useState({
        enabled: true,
        widget_title: "Book your stay",
        primary_color: "#ff7b02",
        cta_text: "Check availability",
        min_nights: 1,
        max_nights: 30,
        currency: "EUR",
        commission_pct: 0,
        confirmation_email: true,
        allow_payment_at_property: true,
        allow_card_payments: true,
        allow_paypal: false,
        embed_domains: "yourhotel.com\nsubdomain.yourhotel.com",
    });

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const save = () => { toast.success("Booking engine settings saved (mocked)"); };

    const embedCode = `<!-- Akomoda Booking Engine -->
<script src="https://cdn.akomoda.co/widget.js" async></script>
<div data-akomoda-widget data-hotel="your-slug" data-color="${form.primary_color}"></div>`;

    return (
        <>
            <Topbar title="Booking Engine" subtitle="Embeddable widget for hotel websites" />
            <div className="flex-1 overflow-auto p-6">
                <div className="mb-4 flex items-center gap-2">
                    <StatusBadge status={form.enabled ? "active" : "skipped"} label={form.enabled ? "Engine enabled" : "Engine disabled"} />
                    <span className="text-[11px] text-muted-foreground">Global default for all hotels</span>
                </div>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <div className="akm-card p-4 lg:col-span-2">
                        <div className="mb-3 akm-section-title">Widget appearance</div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <Field label="Widget title"><input className="akm-input" value={form.widget_title} onChange={(e) => update("widget_title", e.target.value)} /></Field>
                            <Field label="Call to action text"><input className="akm-input" value={form.cta_text} onChange={(e) => update("cta_text", e.target.value)} /></Field>
                            <Field label="Primary color">
                                <div className="flex items-center gap-2">
                                    <input type="color" value={form.primary_color} onChange={(e) => update("primary_color", e.target.value)} className="h-9 w-12 rounded-md border border-border" />
                                    <input className="akm-input flex-1 font-mono" value={form.primary_color} onChange={(e) => update("primary_color", e.target.value)} />
                                </div>
                            </Field>
                            <Field label="Default currency"><input className="akm-input" value={form.currency} onChange={(e) => update("currency", e.target.value)} /></Field>
                        </div>

                        <div className="mt-4 mb-3 akm-section-title">Booking rules</div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <Field label="Min nights"><input type="number" min="1" className="akm-input" value={form.min_nights} onChange={(e) => update("min_nights", Number(e.target.value))} /></Field>
                            <Field label="Max nights"><input type="number" min="1" className="akm-input" value={form.max_nights} onChange={(e) => update("max_nights", Number(e.target.value))} /></Field>
                            <Field label="Platform commission %"><input type="number" step="0.1" className="akm-input" value={form.commission_pct} onChange={(e) => update("commission_pct", Number(e.target.value))} /></Field>
                            <Field label="Allowed embed domains (one per line)">
                                <textarea className="akm-input h-24 py-2 font-mono text-[12px]" value={form.embed_domains} onChange={(e) => update("embed_domains", e.target.value)} />
                            </Field>
                        </div>

                        <div className="mt-4 mb-3 akm-section-title">Payment options</div>
                        <div className="space-y-1">
                            <Toggle label="Send confirmation email" checked={form.confirmation_email} onChange={(v) => update("confirmation_email", v)} />
                            <Toggle label="Allow payment at property" checked={form.allow_payment_at_property} onChange={(v) => update("allow_payment_at_property", v)} />
                            <Toggle label="Allow card payments (Stripe)" checked={form.allow_card_payments} onChange={(v) => update("allow_card_payments", v)} />
                            <Toggle label="Allow PayPal" checked={form.allow_paypal} onChange={(v) => update("allow_paypal", v)} />
                        </div>

                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={() => update("enabled", !form.enabled)} className="akm-btn-ghost">
                                {form.enabled ? "Disable engine" : "Enable engine"}
                            </button>
                            <button onClick={save} className="akm-btn-primary" data-testid="booking-engine-save">Save changes</button>
                        </div>
                    </div>

                    <div className="akm-card p-4">
                        <div className="mb-3 akm-section-title">Embed snippet</div>
                        <pre className="overflow-auto rounded-md border border-border bg-background p-3 text-[11px] font-mono leading-relaxed text-muted-foreground">{embedCode}</pre>
                        <div className="mt-3 text-[11px] text-muted-foreground">
                            Hotels add this snippet to their website. The widget auto-styles itself with the configured primary color and routes bookings to their tenant.
                        </div>

                        <div className="mt-4 mb-3 akm-section-title">Live preview</div>
                        <div className="rounded-md border border-border bg-background p-4">
                            <div className="text-[13px] font-semibold tracking-tight" style={{ color: form.primary_color }}>{form.widget_title}</div>
                            <div className="mt-3 grid grid-cols-2 gap-2">
                                <input className="akm-input h-8 text-[12px]" placeholder="Check-in" />
                                <input className="akm-input h-8 text-[12px]" placeholder="Check-out" />
                            </div>
                            <button className="mt-2 inline-flex h-8 w-full items-center justify-center rounded-md text-[12px] font-medium text-white" style={{ background: form.primary_color }}>
                                {form.cta_text}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}

function Toggle({ label, checked, onChange }) {
    return (
        <label className="flex cursor-pointer items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-[13px]">
            <span>{label}</span>
            <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-[hsl(var(--primary))]" />
        </label>
    );
}
